import axios from "axios";
import React, { useEffect, useState } from "react";
import { API } from "../../../../API";
import { isAutheticated } from "../../../auth/authhelper";
import ClipLoader from "react-spinners/ClipLoader";
import { Link, useParams } from "react-router-dom";
import Footer from "../../Footer";
import getSymbolFromCurrency from "currency-symbol-map";

function ViewProduct() {
  const { productId } = useParams();
  const { token } = isAutheticated();

  const [productLoading, setProductLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [currency, setCurrency] = useState();
  const [taxes, setTaxes] = useState([]);

  const [state, setState] = useState({
    title: "",
    description: "",
    image: "",
    price: "",
    sale_price: "",
    sku: "",
    quantity: "",
    track_quantity: false,
    continue_selling: false,
    status: false,
    categoryObj: null, // full category object
    taxObj: null,
    variants: null, // keep raw variants payload
  });

  const [imagesUrl, setImagesUrl] = useState([]); // previews urls array (image1..image5)
  const [originalVariants, setOriginalVariants] = useState([]);
  const [variantChecked, setVariantChecked] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [imageTitle, setImageTitle] = useState("");

  const defaultImage =
    "https://res.cloudinary.com/duenuiiav/image/upload/v1738589368/tellytell/content_category/tpa6shtnfp9txuekeawo.png";

  // Fetch categories, taxes and user currency concurrently, and product separately
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        setCategoryLoading(true);
        setUserLoading(true);

        const [catRes, taxRes, userRes] = await Promise.all([
          axios.get(`${API}/api/category`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios
            .get(`${API}/api/tax_rates/view_taxs`, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => ({ data: { data: [] } })), // tolerate missing tax endpoint
          axios
            .get(`${API}/api/user`, {
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            })
            .catch(() => null),
        ]);

        const cats =
          Array.isArray(catRes?.data) && catRes.data.length > 0
            ? catRes.data
            : catRes?.data?.categories || [];

        setCategories(cats);
        setTaxes(taxRes?.data?.data || []);
        if (userRes?.data?.data?.settings?.currency) {
          setCurrency(userRes.data.data.settings.currency);
        }
      } catch (err) {
        console.error("Error fetching meta:", err);
      } finally {
        setCategoryLoading(false);
        setUserLoading(false);
      }
    };

    if (token) fetchMeta();
  }, [token]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setProductLoading(true);
        const res = await axios.get(`${API}/api/product/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const product = res?.data?.data || res?.data || {};

        // pick category object from categories list if available
        const categoryObj = categories.find((c) => c._id === product?.category) || null;

        // determine tax object if present
        let taxObj = null;
        if (product?.tax?._id) {
          taxObj = taxes.find((t) => t._id === product.tax._id) || null;
        }

        // prepare imagesUrl array from image1..image5 fields and product.image
        const previews = [];
        for (let i = 1; i <= 5; i++) {
          const key = `image${i}`;
          if (product && product[key]) previews.push(product[key]);
        }

        // set state
        setState((prev) => ({
          ...prev,
          title: product?.title || "",
          description: product?.description || "",
          image: product?.image || "",
          price: product?.price ?? "",
          sale_price: product?.sale_price ?? "",
          sku: product?.sku ?? "",
          quantity: product?.quantity ?? "",
          track_quantity: !!product?.track_quantity,
          continue_selling: !!product?.continue_selling,
          status: !!product?.status,
          categoryObj: categoryObj,
          taxObj: taxObj,
          variants: product?.variants || null,
        }));

        setImagesUrl(previews);

        // normalize variants: product.variants can be either:
        // - an object with arrays (variant_title, variant_price, ...) OR
        // - an array of variant objects (newer API)
        if (product?.variants) {
          setVariantChecked(true);
          if (Array.isArray(product.variants)) {
            setOriginalVariants(product.variants);
          } else if (product.variants.variant_title && Array.isArray(product.variants.variant_title)) {
            const built = product.variants.variant_title.map((title, i) => ({
              variant: title,
              price: product.variants.variant_price?.[i] ?? "",
              quantity: product.variants.variant_quantity?.[i] ?? "",
              SKU: product.variants.variant_sku?.[i] ?? "",
              image: product.variants.variant_image?.[i] ?? "",
            }));
            setOriginalVariants(built);
          } else {
            // fallback: push raw variants object
            setOriginalVariants([product.variants]);
          }
        } else {
          setVariantChecked(false);
          setOriginalVariants([]);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setProductLoading(false);
      }
    };

    if (token && productId) fetchProduct();
    // Note: categories and taxes could be fetched after product; product fetch can run independently.
    // If you want product's categoryName resolved after categories load, consider re-running product fetch when `categories` changes.
    // For now, categoryObj is best-effort lookup.
  }, [token, productId, categories, taxes]);

  // ensure quantity default
  useEffect(() => {
    if (state.quantity === "" || state.quantity == null) {
      setState((prev) => ({ ...prev, quantity: "0" }));
    }
  }, [state.quantity]);

  const openImage = (imageUrl, title = "Image") => {
    setCurrentImage(imageUrl);
    setImageTitle(title);
    setOpenModal(true);
  };

  if (productLoading || categoryLoading || userLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="text-center p-4 border border-primary rounded shadow">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3 fs-5 text-primary">Product Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* page title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0">View Product</h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard">SoComOTT</Link>
                    </li>
                    <li className="breadcrumb-item active">OTT Shop</li>
                    <li className="breadcrumb-item active">View Product</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* back button */}
          <div className="row">
            <div className="col-12 text-right mb-3">
              <Link to="/comproducts">
                <button type="button" className="btn btn-outline-secondary">
                  Back to Products
                </button>
              </Link>
            </div>
          </div>

          {/* Row 1: details & meta */}
          <div className="row">
            {/* Left: details */}
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3">Product Details</h5>
                  <div className="mb-3">
                    <strong>Title:</strong> <div className="d-inline ms-2">{state.title || "-"}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Description:</strong>
                    <div className="mt-2" style={{ whiteSpace: "pre-wrap" }}>
                      {state.description || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: category, tax, status */}
            <div className="col-lg-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3">Product Info</h5>
                  <div className="mb-2">
                    <strong>Category:</strong>{" "}
                    <span className="ms-2">{state.categoryObj?.category || "-"}</span>
                  </div>

                  <div className="mb-2">
                    <strong>Tax:</strong>{" "}
                    <span className="ms-2">{state.taxObj ? `${state.taxObj.tax_name} (${state.taxObj.tax_percentage}%)` : "-"}</span>
                  </div>

                  <div className="mb-2">
                    <strong>Status:</strong>{" "}
                    <span className="ms-2">{state.status ? "Active" : "Inactive"}</span>
                  </div>

                  <div className="mt-3">
                    <strong>Featured Image:</strong>
                    <div className="mt-2 text-center">
                      <img
                        src={state.image || defaultImage}
                        alt="featured"
                        style={{ width: 120, height: 90, objectFit: "cover", cursor: "pointer" }}
                        onClick={() => openImage(state.image || defaultImage, "Featured Image")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: images */}
          <div className="row mt-3">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  <h5>Product Images</h5>
                  <div className="d-flex flex-wrap mt-2">
                    {imagesUrl.length > 0 ? (
                      imagesUrl.map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt={`img-${i}`}
                          style={{ width: 90, height: 70, objectFit: "cover", marginRight: 8, marginBottom: 8, cursor: "pointer" }}
                          onClick={() => openImage(img, `Image ${i + 1}`)}
                        />
                      ))
                    ) : (
                      <div className="text-muted">No additional images</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: pricing and variants */}
          <div className="row mt-3">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  {!variantChecked ? (
                    <>
                      <h5>Pricing & Stock</h5>
                      <div className="row">
                        <div className="col-md-4 mb-2">
                          <strong>Price:</strong>
                          <div className="ms-2">{state.price ?? "-"}</div>
                        </div>
                        <div className="col-md-4 mb-2">
                          <strong>Sale Price:</strong>
                          <div className="ms-2">{state.sale_price ?? "-"}</div>
                        </div>
                        <div className="col-md-4 mb-2">
                          <strong>SKU:</strong>
                          <div className="ms-2">{state.sku ?? "-"}</div>
                        </div>
                        <div className="col-md-4 mb-2">
                          <strong>Quantity:</strong>
                          <div className="ms-2">{state.quantity ?? "0"}</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h5>Variants</h5>
                      {originalVariants.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-striped">
                            <thead>
                              <tr>
                                <th>Variant</th>
                                <th>Price</th>
                                <th>Quantity</th>
                                <th>SKU</th>
                                <th>Image</th>
                              </tr>
                            </thead>
                            <tbody>
                              {originalVariants.map((v, idx) => (
                                <tr key={idx}>
                                  <td>{v.variant || v.variant_title || `Variant ${idx + 1}`}</td>
                                  <td>{v.price ?? v.variant_price ?? "-"}</td>
                                  <td>{v.quantity ?? v.variant_quantity ?? "-"}</td>
                                  <td>{v.SKU ?? v.variant_sku ?? "-"}</td>
                                  <td>
                                    {v.image ? (
                                      <img
                                        src={v.image}
                                        alt={v.variant || `var-${idx}`}
                                        style={{ width: 60, height: 60, objectFit: "cover", cursor: "pointer" }}
                                        onClick={() => openImage(v.image, v.variant || `Variant ${idx + 1}`)}
                                      />
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div>No variants data available</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Image Modal */}
          {openModal && (
            <div
              className="modal fade show"
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{imageTitle}</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setOpenModal(false)} />
                  </div>
                  <div className="modal-body text-center">
                    <img src={currentImage || defaultImage} alt={imageTitle} style={{ maxWidth: "100%", height: "auto" }} />
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setOpenModal(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ViewProduct;
